import { useEffect, useState, useRef, lazy, Suspense } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import { useProjectDetailQuery, usePortalInvalidate } from "@/hooks/usePortalQueries";
import { RecordFormModal } from "@/components/RecordFormModal";
import { ArchiveControls } from "@/components/ArchiveControls";
import { SectionPanel } from "@/components/Section";
import { StageStepper } from "@/components/StageGlyph";
import { ProjectSnapshotHeader } from "@/components/project/ProjectSnapshotHeader";
import { ProjectPageTabs, PROJECT_TABS } from "@/components/project/ProjectPageTabs";
import { EmptyState } from "@/components/EmptyState";
import { DetailSkeleton } from "@/components/Skeletons";
import { formatMoney } from "@/lib/money";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent } from "@/components/ui/dropdown-menu";
import { NotionSyncBanner } from "@/components/NotionSyncBanner";
import { MapPin, User, Mail, Receipt, BookOpen, FileText, Pencil, CheckCircle2, Loader2, HandCoins, MoreHorizontal, ChevronDown } from "lucide-react";

// Phase 4 (2026-10, Design System 2.0 visual reskin): every tab's own heavy sub-component is
// now React.lazy — an unopened tab (e.g. History, Deliverables) never pulls its JS at all.
// Recce Wave 1 nav fix (2026-10): the Recce tab is back to rendering INLINE (its own simple
// index via RecceDashboard.jsx — title/date/status/locations/photos/updated, "Open Recce" is
// the only way into the full-screen /projects/:id/recce workspace) instead of navigating away
// on click.
const ProjectActivityFeed = lazy(() => import("@/components/project/ProjectActivityFeed").then((m) => ({ default: m.ProjectActivityFeed })));
const ProjectTasksSection = lazy(() => import("@/components/tasks/ProjectTasksSection").then((m) => ({ default: m.ProjectTasksSection })));
const AssignmentEngine = lazy(() => import("@/components/AssignmentEngine").then((m) => ({ default: m.AssignmentEngine })));
const ProjectFilesDocumentsPanel = lazy(() => import("@/components/project/ProjectFilesDocumentsPanel").then((m) => ({ default: m.ProjectFilesDocumentsPanel })));
const WorkingDocPanel = lazy(() => import("@/components/WorkingDocPanel").then((m) => ({ default: m.WorkingDocPanel })));
const FilmsDeliverables = lazy(() => import("@/components/FilmsDeliverables").then((m) => ({ default: m.FilmsDeliverables })));
const ReviewLinks = lazy(() => import("@/components/ReviewLinks").then((m) => ({ default: m.ReviewLinks })));
const RecommendedVideos = lazy(() => import("@/components/RecommendedVideos").then((m) => ({ default: m.RecommendedVideos })));
const ClientPortalMessagesCard = lazy(() => import("@/components/ClientPortalMessagesCard").then((m) => ({ default: m.ClientPortalMessagesCard })));
const ProjectComments = lazy(() => import("@/components/ProjectComments").then((m) => ({ default: m.ProjectComments })));
const ProjectBtsGallery = lazy(() => import("@/components/project/ProjectBtsGallery").then((m) => ({ default: m.ProjectBtsGallery })));
const AgencyPanel = lazy(() => import("@/components/project/AgencyPanel"));
const ProjectMeetingsPanel = lazy(() => import("@/components/project/ProjectMeetingsPanel").then((m) => ({ default: m.ProjectMeetingsPanel })));
const ClientPortalPmCard = lazy(() => import("@/components/ClientPortalPmCard").then((m) => ({ default: m.ClientPortalPmCard })));
const ProjectHistoryTimeline = lazy(() => import("@/components/project/ProjectHistoryTimeline").then((m) => ({ default: m.ProjectHistoryTimeline })));
const ArchivedTasksPanel = lazy(() => import("@/components/project/ArchivedTasksPanel").then((m) => ({ default: m.ArchivedTasksPanel })));
const RecceDashboard = lazy(() => import("@/components/project/recce/RecceDashboard").then((m) => ({ default: m.RecceDashboard })));

const TabFallback = () => <div className="text-[12.5px] text-faint py-10 text-center" data-testid="project-tab-loading">Loading…</div>;

// Project Page Redesign (2026-02, approved via Claude Design mockup) — six founder-approved
// fixes built in this pass: (1) Build Quote / Draft Email now route to distinct destinations,
// Archive/Purge moved into a ⋯ overflow menu; (2) the old empty "Action Centre — coming in P2"
// box is now a small muted caption, never an empty panel; (3) History (retrospective: Timeline
// · Lessons Learned · Archived Tasks) and Activity (live: Tasks/Comments/Messages/Documents,
// kernel/project_activity.py) no longer overlap by construction; (4) a real "Behind the Scenes"
// staff gallery (B1+B2 of the BTS feature — partner/freelancer upload + Post-to-Floor are
// explicit follow-ups, not built this pass); (5) one canonical Project Files (Drive) home,
// cross-linked from both Workspace and Commercial; (6) Internal Cost/Margin stay gated to
// Founder/Finance/Partner/PM via the SAME server-side FIELD_GATES as before — freelancer/
// production_partner/client never receive those fields in the API response at all, this page
// just renders whatever the backend sent.
// Phase 4 (2026-10, Design System 2.0 visual reskin): "Action Centre arrives in P2" caption
// removed, local `Section` swapped for the shared `SectionPanel` primitive, StaffTimelineRail
// swapped for the shared StageStepper (same /timeline stage data/computation, unchanged —
// only which component renders it changed), header actions reordered to the approved spec.
// Zero data/API/permission/route change beyond the Recce tab's own panel content (see above).
export default function ProjectDashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightAssignmentId = searchParams.get("assignment");
  const { hasScope, user } = useAuth();
  const canSeeRecommendedVideos = hasScope("video_links.read");
  const canSeeReviewLinks = hasScope("review_links.read");
  const canSeeFilmsDeliverables = hasScope("films_deliverables.read");
  // Wave 1 (2026-10, frontend data cache) — project detail now rides the shared TanStack
  // cache (30s staleTime): opening a project you already visited this session shows it
  // INSTANTLY from cache while a quiet background refresh checks for changes, instead of a
  // skeleton every single time. `reloadProject` replaces the old manual `load()`; a failed
  // BACKGROUND refresh keeps showing the cached project (banner below) instead of blanking.
  const { data, isLoading: loading, isError, error: queryError, fetchStatus, refetch: reloadProject } = useProjectDetailQuery(id);
  const invalidatePortal = usePortalInvalidate();
  // Cursor re-review PR #22, item 1: a 5xx/network first-load error is NOT the same as a real
  // 403/404 — only those two mean "you truly can't see this", everything else just means the
  // load failed and deserves a Retry, not a permanent "Project not found".
  // Cursor re-review PR #23 (Part 5), item 3: being OFFLINE on first load is not an `isError`
  // state at all — react-query just pauses the fetch (`fetchStatus === "paused"`), so `loading`
  // stayed true forever and this was a permanent skeleton with no error, no Retry.
  // Part 6 polish (2026-10): this is its own "offline" state, not the generic "load_error" —
  // a Retry button implies the SAME request would succeed if clicked again, which is
  // misleading while genuinely offline (the browser just queued/paused the fetch).
  const errStatus = queryError?.response?.status;
  const error = isError && !data
    ? (errStatus === 403 ? "restricted" : errStatus === 404 ? "not_found" : "load_error")
    : (!data && fetchStatus === "paused" ? "offline" : null);
  const showStaleRefreshNotice = isError && !!data;
  const [editing, setEditing] = useState(false);
  // Client Point of Contact (2026-09): "+ Add new contact" inside the project-edit form opens
  // this as a SIBLING modal (never nested inside RecordFormModal itself), pre-filled to the
  // client currently selected in that form.
  const [addContactForClient, setAddContactForClient] = useState(null);
  const [markingWon, setMarkingWon] = useState(null);
  const [creatingAssignment, setCreatingAssignment] = useState(null);
  const [activeTab, setActiveTabState] = useState(() => {
    // Code-review fix (2026-10, Cursor audit item 8) — an arbitrary/stale `?tab=` (a bad
    // deep-link, or an older tab key that no longer exists) used to set `activeTab` to a
    // value that matches NONE of the panels below, rendering a blank page under the tab bar.
    const t = searchParams.get("tab");
    return VALID_TABS.includes(t) ? t : "overview";
  });
  // Agency Portal Stabilisation Part B (2026-10), item 1 — unread badge on the "Communication"
  // tab itself (visible BEFORE clicking in, unlike AgencyThreadsPanel's own in-panel badge,
  // which only renders once you're already on that tab). Refetches whenever `activeTab`
  // changes so it clears right after AgencyThreadsPanel's own mark-read call fires.
  const [agencyUnread, setAgencyUnread] = useState(0);
  useEffect(() => {
    if (!hasScope("client_portal.admin")) return;
    api.get(`/v1/build/projects/${id}/agency-comments/unread-count`).then((r) => setAgencyUnread(r.data.count || 0)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, activeTab]);
  // Perf fix (2026-10, Cursor audit item 8 / group 0B): the active tab now lives in the URL
  // (?tab=) so Back/refresh/deep-links keep it, same as Client Portal's own ?scrollTo= pattern.
  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set("tab", tab); return next; }, { replace: true });
  };
  // Recce Wave 1 nav fix (2026-10): the Recce tab is a plain panel again, same as every other
  // PROJECT_TABS entry — it no longer navigates away on click. Only "Open Recce" inside that
  // panel (RecceDashboard.jsx) navigates to the full-screen /projects/:id/recce editor.
  const onTabChange = (key) => setActiveTab(key);
  const [timelineStages, setTimelineStages] = useState([]);
  const [showOlderCommercial, setShowOlderCommercial] = useState(false);
  const assignmentsRef = useRef(null);

  const [documents, setDocuments] = useState([]);
  const loadDocuments = () => api.get(`/v1/commercial-docs/documents?project_id=${id}`).then((r) => setDocuments(r.data.items || [])).catch(() => setDocuments([]));
  // Sprint 2 (2026-02): per-project Saved Drafts — reuses the same GET /quote/drafts endpoint
  // the Quote Builder's own global "My Drafts" panel calls, just scoped with ?project_id so a
  // PM can find a WIP quote for THIS project without hunting through every draft ever saved.
  const [drafts, setDrafts] = useState([]);
  const loadDrafts = () => api.get(`/v1/commercial/quote/drafts?project_id=${id}`).then((r) => setDrafts(r.data.items || [])).catch(() => setDrafts([]));
  const [uploadingLegacy, setUploadingLegacy] = useState(false);
  const legacyInputRef = useRef(null);
  const uploadLegacyDoc = async (file) => {
    if (!file) return;
    setUploadingLegacy(true);
    try {
      const form = new FormData();
      form.append("project_id", id);
      form.append("file", file);
      const r = await api.post("/v1/commercial-docs/documents/legacy-upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded to the project's Drive folder", { action: r.data.drive_web_link ? { label: "Open", onClick: () => window.open(r.data.drive_web_link, "_blank") } : undefined });
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (e?.response?.status === 503 || code === "drive_not_configured") toast.error("Drive isn't connected yet — pending the Workspace admin's setup");
      else if (code === "project_missing_drive_folder") toast.error("This project has no Drive folder linked yet");
      else toast.error(code || "Couldn't upload to Drive");
    } finally {
      setUploadingLegacy(false);
      if (legacyInputRef.current) legacyInputRef.current.value = "";
    }
  };

  useEffect(() => {
    setDocumentsLoaded(false);
    setDraftsLoaded(false);
    api.get(`/v1/projects/${id}/timeline`).then((r) => setTimelineStages(r.data.stages || [])).catch(() => setTimelineStages([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Perf fix (2026-10, Cursor audit item 8 / group 0B): documents and drafts used to be
  // fetched eagerly on every project load even if the user never opens Workspace/Commercial.
  // Now each fetches once, the first time its own tab becomes active (and again whenever `id`
  // changes) — the same `loadDocuments`/`loadDrafts` calls, just deferred and lazy.
  const [documentsLoaded, setDocumentsLoaded] = useState(false);
  useEffect(() => {
    if (activeTab === "workspace" && !documentsLoaded) { loadDocuments(); setDocumentsLoaded(true); }
  }, [activeTab, documentsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const [draftsLoaded, setDraftsLoaded] = useState(false);
  useEffect(() => {
    if (activeTab === "commercial" && !draftsLoaded) { loadDrafts(); setDraftsLoaded(true); }
  }, [activeTab, draftsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep-link from the Dashboard's "Pending Partner Decisions" widget (?assignment={id}) —
  // jump straight to the Workspace tab and scroll down into Crew/Assignments once the page has
  // rendered, same UX as Phase 5's "Create Production Assignment" scroll-on-success.
  useEffect(() => {
    if (highlightAssignmentId && data) {
      setActiveTab("workspace");
      const t = setTimeout(() => assignmentsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
      return () => clearTimeout(t);
    }
  }, [highlightAssignmentId, data]); // eslint-disable-line react-hooks/exhaustive-deps

  const markWon = async (quoteId) => {
    setMarkingWon(quoteId);
    try {
      await api.post(`/v1/commercial/quotes/${quoteId}/mark-won`);
      toast.success("Quote marked Won — Generate SOW is now available");
      await reloadProject();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || "Couldn't mark the quote Won");
    } finally { setMarkingWon(null); }
  };

  // Phase 5 of the Operational Control Upgrade (2026-02): Commercial Engine -> Production
  // Partner handoff. Only ever available on a Won quote when the Project's own Production
  // Model is "Production Partner Managed" (server also enforces this — this is UX gating
  // only). Drafts a Partner Budget Assignment (Approval Card, nothing auto-sent) using the
  // INTERNAL production allocation only, never the client quote value, then jumps the PM to
  // the Workspace tab's existing Crew/Assignments section to review.
  const createProductionAssignment = async (quoteId) => {
    setCreatingAssignment(quoteId);
    try {
      await api.post(`/v1/commercial/quotes/${quoteId}/create-production-assignment`);
      toast.success("Draft Partner Budget Assignment submitted for approval — review it in the Workspace tab's Crew/Assignments once approved.");
      setActiveTab("workspace");
      setTimeout(() => assignmentsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Couldn't create the production assignment");
    } finally { setCreatingAssignment(null); }
  };

  // Cursor re-review PR #22, item 1 (regression): `error` MUST be checked before the skeleton
  // — the old `loading || !data` skeleton check ran first and `!data` stays true forever once
  // a 403/404 clears the cache (useCachedQuery item 2), so the error state below never
  // rendered; this was a permanent skeleton on every first-load 403/404.
  if (error) return (
    <div className="p-8 text-sm text-faint" data-testid="project-dashboard-error">
      {error === "restricted" ? "Access Restricted" : error === "not_found" ? "Project not found" : error === "offline" ? (
        <>You're offline. This will reload automatically once you're back online, or <button onClick={() => reloadProject()} data-testid="project-dashboard-error-retry" className="font-semibold underline text-ink-soft">try now</button>.</>
      ) : (
        <>Couldn't load this project. <button onClick={() => reloadProject()} data-testid="project-dashboard-error-retry" className="font-semibold underline text-ink-soft">Retry</button></>
      )}
    </div>
  );
  if (loading || !data) return <DetailSkeleton testId="project-dashboard-skeleton" />;

  const p = data.project;
  const client = data.client;
  const canEdit = hasScope("projects.create");
  const canManageClientDocs = hasScope("client_portal.admin");
  // Wave 1 (2026-10, frontend data cache) point 3 — a failed BACKGROUND refresh (stale
  // cached project already on screen) must never blank the page, just show a small notice.
  // Code-review fix (2026-10, Cursor audit P3-8): a project edit must also invalidate the
  // cached CLIENT detail — budget/status/name shown there can change as a result.
  const reloadProjectAndLists = async () => { await reloadProject(); invalidatePortal.projectLists(); if (client?.id) invalidatePortal.client(client.id); };
  const driveUrl = hasContent(p.drive) ? p.drive : (hasContent(p.drive_folder) ? p.drive_folder : null);
  // Project Workspace Phase 1 (2026-09), §4 — view-layer merge only, no quote math touched:
  // combine quotes + drafts into one recency-sorted timeline for the "current prominent,
  // rest under a fold" Commercial tab treatment.
  const commercialItems = [
    ...data.quotes.map((q) => ({ type: "quote", item: q, date: q.date || q.created_at || "" })),
    ...drafts.map((d) => ({ type: "draft", item: d, date: d.updated_at || d.created_at || "" })),
  ].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  const currentCommercial = commercialItems[0];
  const olderCommercial = commercialItems.slice(1);
  const currentStage = timelineStages.find((s) => s.state === "current")?.label;

  return (
    <div className="p-8 pb-24 space-y-3" data-testid="project-dashboard-page">
      {showStaleRefreshNotice && (
        <div className="flex items-center justify-between rounded-md border border-tone-gold-bg bg-tone-gold-bg px-3.5 py-2 text-[12.5px] text-tone-gold-text" data-testid="project-dashboard-refresh-error">
          <span>Showing a saved copy — unable to refresh just now.</span>
          <button onClick={() => reloadProject()} data-testid="project-dashboard-refresh-retry" className="font-semibold underline">Retry</button>
        </div>
      )}
      <NotionSyncBanner sync={p.sync} role={user?.role} recordId={p.id} onRetried={reloadProjectAndLists} testIdPrefix="project-notion-sync" />
      <ProjectSnapshotHeader
        project={p} client={client} stages={timelineStages} onAssignPm={() => setEditing(true)}
        actions={
          <>
            {canEdit && (
              <button onClick={() => setEditing(true)} data-testid="project-edit-btn"
                className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-paper px-3.5 py-2 text-[12.5px] font-semibold text-ink-soft hover:border-brand">
                <Pencil className="h-3.5 w-3.5" /> Edit
              </button>
            )}
            <Link to={`/commercial?project_id=${p.id}&client_id=${client?.id || ""}&focus=email`} data-testid="project-draft-email-btn"
              className="inline-flex items-center gap-1.5 rounded-md bg-ink px-3.5 py-2 text-[12.5px] font-semibold text-paper hover:bg-ink-deep">
              <Mail className="h-3.5 w-3.5" /> Draft Email
            </Link>
            <Link to={`/commercial?project_id=${p.id}&client_id=${client?.id || ""}`} data-testid="project-build-quote-btn"
              className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3.5 py-2 text-[12.5px] font-semibold text-ink hover:bg-brand-dim">
              <Receipt className="h-3.5 w-3.5" /> Build Quote
            </Link>
            {(canEdit || user?.role === "founder") && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="project-overflow-menu-btn" className="inline-flex items-center justify-center rounded-md border border-hairline bg-paper h-[38px] w-[38px] hover:border-brand">
                    <MoreHorizontal className="h-4 w-4 text-ink-soft" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 p-1.5 rounded-md border-hairline">
                  <div className="px-2.5 pt-1 pb-1.5 text-[10px] font-bold uppercase tracking-wide text-faint">Project Admin</div>
                  <ArchiveControls
                    entity="projects" variant="menu"
                    record={{ id: p.id, name: p.name, archived: p.archived }}
                    canWrite={canEdit} isFounder={user?.role === "founder"}
                    onChanged={reloadProjectAndLists}
                    onPurged={() => { invalidatePortal.projectLists(); invalidatePortal.removeProjectDetails([p.id]); if (client?.id) invalidatePortal.client(client.id); navigate("/projects"); }}
                  />
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </>
        }
      />

      <div className="rounded-md border border-hairline bg-paper px-6 py-5 overflow-x-auto" data-testid="project-stage-stepper-wrap">
        <StageStepper stage={currentStage} testId="project-stage-stepper" />
      </div>

      <ProjectPageTabs active={activeTab} onChange={onTabChange} tabs={hasScope("client_portal.admin") ? PROJECT_TABS : PROJECT_TABS.filter((t) => t.key !== "recce")} badges={{ communication: agencyUnread }} />

      {activeTab === "overview" && (
        <div className="space-y-4 pt-1" data-testid="project-tab-panel-overview">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <WhatsNextCard project={p} stages={timelineStages} projectId={p.id} crewAssignments={data.crew_assignments} />
            <SectionPanel title="Brief" testId="project-brief-section">
              <div className="divide-y divide-hairline">
                <div className="px-5 py-3.5" data-testid="project-brief-objective">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-faint">Objective</div>
                  <div className="text-[13.5px] text-ink-soft leading-relaxed mt-1">{hasContent(p.objective) ? p.objective : <EmptyState compact title="No objective set" hint="Use Edit above to add one." testId="project-objective-empty" />}</div>
                </div>
                <div className="px-5 py-3.5" data-testid="project-brief-deliverables">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-faint">Deliverables Summary</div>
                  <div className="text-[13.5px] text-ink-soft leading-relaxed whitespace-pre-wrap mt-1">{hasContent(p.deliverables) ? p.deliverables : (hasContent(p.deliverables_summary) ? p.deliverables_summary : <EmptyState compact title="No deliverables listed" hint="Use Edit above to add them." testId="project-deliverables-empty" />)}</div>
                </div>
                {BRIEF_NOTES_FIELDS.filter(([key]) => hasContent(p[key])).map(([key, label]) => (
                  <div key={key} className="px-5 py-3.5" data-testid={`project-brief-${key}`}>
                    <div className="text-[10px] font-bold uppercase tracking-wide text-faint">{label}</div>
                    <div className="text-[13.5px] text-ink-soft whitespace-pre-wrap mt-1">{p[key]}</div>
                  </div>
                ))}
              </div>
            </SectionPanel>
          </div>
        </div>
      )}

      {activeTab === "activity" && (
        <div className="pt-1" data-testid="project-tab-panel-activity">
          <Suspense fallback={<TabFallback />}><ProjectActivityFeed projectId={p.id} /></Suspense>
        </div>
      )}

      {activeTab === "workspace" && (
        <div className="space-y-4 pt-1" data-testid="project-tab-panel-workspace">
          <Suspense fallback={<TabFallback />}><ProjectTasksSection projectId={p.id} /></Suspense>
          <div ref={assignmentsRef} data-testid="project-assignments-section-anchor">
            <Suspense fallback={<TabFallback />}><AssignmentEngine projectId={p.id} productionModel={p.production_model} highlightAssignmentId={highlightAssignmentId} compact /></Suspense>
          </div>
          <Suspense fallback={<TabFallback />}>
            <ProjectFilesDocumentsPanel
              project={p} canEdit={canEdit} canManageClientDocs={canManageClientDocs} clientName={client?.name} driveUrl={driveUrl}
              commercialDocs={documents} uploadingLegacy={uploadingLegacy} onUploadLegacy={uploadLegacyDoc} legacyInputRef={legacyInputRef}
              initialDocumentCount={data.counts?.documents}
            />
          </Suspense>
          {hasContent(p.timeline_notes) ? (
            <SectionPanel title="Production Notes" action={<span className="text-[10px] font-normal tracking-normal uppercase text-faint">Read-only · Synced from Notion</span>} testId="project-production-notes-section">
              <div className="px-5 py-3.5 text-[13.5px] text-ink-soft whitespace-pre-wrap" data-testid="project-production-notes">{p.timeline_notes}</div>
            </SectionPanel>
          ) : (
            <SectionPanel title="Production Notes" action={<span className="text-[10px] font-normal tracking-normal uppercase text-faint">Read-only · Synced from Notion</span>} testId="project-production-notes-section">
              <div className="px-5 py-3.5"><EmptyState compact title="Nothing written yet" hint="Notes typed in the Notion page appear here automatically." testId="project-production-notes-empty" /></div>
            </SectionPanel>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {hasContent(p.shoot_locations) && (
              <SectionPanel title="Shoot Locations" testId="project-shoot-locations-section">
                <div className="px-5 py-4 text-[13.5px] text-ink-soft inline-flex items-start gap-1.5"><MapPin className="h-4 w-4 mt-0.5 shrink-0" /> {p.shoot_locations}</div>
              </SectionPanel>
            )}
            {hasContent(p.equipment_notes) && (
              <SectionPanel title="Equipment" testId="project-equipment-section">
                <div className="px-5 py-3.5 text-[13.5px] text-ink-soft whitespace-pre-wrap" data-testid="project-equipment-notes">{p.equipment_notes}</div>
              </SectionPanel>
            )}
          </div>
          <Suspense fallback={<TabFallback />}><WorkingDocPanel projectId={p.id} /></Suspense>
        </div>
      )}

      {activeTab === "recce" && hasScope("client_portal.admin") && (
        <div className="pt-1" data-testid="project-tab-panel-recce">
          <Suspense fallback={<TabFallback />}><RecceDashboard projectId={p.id} /></Suspense>
        </div>
      )}

      {activeTab === "deliverables" && (
        <div className="space-y-4 pt-1" data-testid="project-tab-panel-deliverables">
          <Suspense fallback={<TabFallback />}>
            {canSeeFilmsDeliverables && <FilmsDeliverables projectId={p.id} projectCategory={p.category} canWrite={hasScope("films_deliverables.write")} />}
            {canSeeReviewLinks && <ReviewLinks projectId={p.id} canWrite={hasScope("review_links.write")} />}
            {canSeeRecommendedVideos && <RecommendedVideos projectId={p.id} projectCategory={p.category} canWrite={hasScope("video_links.write")} />}
          </Suspense>
        </div>
      )}

      {activeTab === "communication" && (
        <div className="space-y-4 pt-1" data-testid="project-tab-panel-communication">
          <Suspense fallback={<TabFallback />}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {hasScope("client_portal.admin") && <ClientPortalMessagesCard projectId={p.id} />}
              <ProjectComments projectId={p.id} />
            </div>
            {hasScope("client_portal.admin") && <ProjectBtsGallery projectId={p.id} driveUrl={driveUrl} />}
            {hasScope("client_portal.admin") && <AgencyPanel projectId={p.id} />}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ProjectMeetingsPanel meetings={data.meetings} canManageVisibility={hasScope("client_portal.admin")} />
              <SectionPanel title="Client Point of Contact" testId="project-contact-section">
                {data.primary_contact ? (
                  <div className="px-5 py-3.5">
                    <div className="text-[13.5px] text-ink inline-flex items-center gap-1.5"><User className="h-3.5 w-3.5 text-faint" /> {data.primary_contact.name}</div>
                    <div className="text-[10.5px] text-faint mt-0.5">{data.primary_contact.role || data.primary_contact.role_title}</div>
                    {data.primary_contact.email && <a href={`mailto:${data.primary_contact.email}`} className="text-xs text-ink-soft hover:text-gold-text">{data.primary_contact.email}</a>}
                  </div>
                ) : <EmptyState compact title="No point of contact set" hint="Use Edit above to assign one." testId="project-contact-empty" />}
              </SectionPanel>
            </div>
            {hasScope("client_portal.admin") && <ClientPortalPmCard projectId={p.id} />}
          </Suspense>
        </div>
      )}

      {activeTab === "commercial" && (
        <div className="space-y-4 pt-1" data-testid="project-tab-panel-commercial">
          <div className={`grid grid-cols-1 sm:grid-cols-2 ${p.internal_cost !== undefined ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-3.5`}>
            <Stat label="Budget (Client)" value={formatMoney(p.budget, p.currency)} />
            {p.internal_cost !== undefined && <Stat label="Internal Cost" value={formatMoney(p.internal_cost, p.currency)} sub={p.margin !== undefined ? `Margin ${p.margin}%` : undefined} gated />}
            <Stat label="Currency" value={p.currency || "—"} />
            <Stat label="Payment Status" value={p.payment_status || "—"} />
          </div>
          {p.internal_cost !== undefined && <div className="text-[11px] text-faint" data-testid="project-commercial-internal-caption">Internal Cost &amp; Margin are visible to you as {user?.role} — hidden from freelancers and production partners.</div>}
          <SectionPanel title="Quotes &amp; Drafts" action={commercialItems.length > 1 ? <span className="text-[10px] font-normal tracking-normal uppercase text-faint">{commercialItems.length} total</span> : undefined} testId="project-commercial-section">
            {commercialItems.length === 0 ? (
              <EmptyState compact title="No quotes or drafts yet" hint="Use Build Quote above to create one." testId="project-quotes-empty" />
            ) : (
              <div className="divide-y divide-hairline">
                {currentCommercial.type === "quote"
                  ? <QuoteRow q={currentCommercial.item} p={p} client={client} user={user} markingWon={markingWon} markWon={markWon} creatingAssignment={creatingAssignment} createProductionAssignment={createProductionAssignment} />
                  : <DraftRow d={currentCommercial.item} />}
                {olderCommercial.length > 0 && (
                  <div className="px-5 py-2">
                    <button onClick={() => setShowOlderCommercial((v) => !v)} data-testid="project-commercial-older-toggle"
                      className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-faint hover:text-ink-soft">
                      <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showOlderCommercial ? "rotate-180" : ""}`} /> {showOlderCommercial ? "Hide" : "Show"} {olderCommercial.length} older
                    </button>
                  </div>
                )}
                {showOlderCommercial && olderCommercial.map((c) => (
                  c.type === "quote"
                    ? <QuoteRow key={`q-${c.item.id}`} q={c.item} p={p} client={client} user={user} markingWon={markingWon} markWon={markWon} creatingAssignment={creatingAssignment} createProductionAssignment={createProductionAssignment} />
                    : <DraftRow key={`d-${c.item.id}`} d={c.item} />
                ))}
              </div>
            )}
          </SectionPanel>
          <div className="text-[11.5px] text-faint">
            SOW PDFs and client documents now live in <button onClick={() => setActiveTab("workspace")} data-testid="project-commercial-files-crosslink" className="font-semibold text-ink-soft hover:text-gold-text underline">Files &amp; Documents → Workspace tab</button>.
          </div>
        </div>
      )}

      {activeTab === "history" && (
        <div className="space-y-4 pt-1" data-testid="project-tab-panel-history">
          <div className="rounded-md bg-ink px-5 py-4 flex items-center gap-3" data-testid="project-history-retrospective-banner">
            <span className="rounded-full bg-brand px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-ink">Retrospective</span>
            <span className="text-[12.5px] text-paper/80">Timeline, Lessons Learned and Archived Tasks are this project's closed record — live updates stay in Activity.</span>
          </div>
          <Suspense fallback={<TabFallback />}>
            <ProjectHistoryTimeline projectId={p.id} />
            <SectionPanel title="Lessons Learned" testId="project-lessons-section">
              {data.lessons.length === 0 ? <EmptyState compact title="No lessons logged yet" testId="project-lessons-empty" /> : (
                <div className="divide-y divide-hairline">
                  {data.lessons.map((l, i) => (
                    <div key={l.id || i} className="px-5 py-3.5 text-[13.5px] text-ink inline-flex items-start gap-1.5"><BookOpen className="h-3.5 w-3.5 mt-0.5 shrink-0 text-faint" /> {l.name || l.title}</div>
                  ))}
                </div>
              )}
            </SectionPanel>
            {hasContent(p.internal_retrospective) && (
              <SectionPanel title="Internal Retrospective" testId="project-internal-retrospective-section">
                <div className="px-5 py-4 text-[13.5px] text-ink-soft whitespace-pre-wrap" data-testid="project-internal-retrospective">{p.internal_retrospective}</div>
              </SectionPanel>
            )}
            {HISTORY_NOTES_FIELDS.some(([key]) => hasContent(p[key])) && (
              <SectionPanel title="Additional Notes" testId="project-additional-notes-section">
                <div className="divide-y divide-hairline">
                  {HISTORY_NOTES_FIELDS.filter(([key]) => hasContent(p[key])).map(([key, label]) => (
                    <div key={key} className="px-5 py-3.5" data-testid={`project-note-${key}`}>
                      <div className="text-[10px] font-bold uppercase tracking-wide text-faint">{label}</div>
                      <div className="text-[13.5px] text-ink-soft whitespace-pre-wrap mt-1">{p[key]}</div>
                    </div>
                  ))}
                </div>
              </SectionPanel>
            )}
            <ArchivedTasksPanel projectId={p.id} />
          </Suspense>
        </div>
      )}

      <RecordFormModal
        type="project"
        mode="edit"
        open={editing}
        onClose={() => setEditing(false)}
        recordId={p.id}
        initialValues={{
          name: p.name, status: p.status, category: p.category, currency: p.currency, budget: p.budget,
          shoot_dates: { start: p.shoot_dates_start || null, end: p.shoot_dates_end || null },
          objective: p.objective, deliverables: p.deliverables || p.deliverables_summary,
          business_problem: p.business_problem,
          primary_contact_id: data.primary_contact?.id || "", drive: p.drive || p.drive_folder,
          production_model: p.production_model || "", production_partner_id: data.production_partner?.id || "",
          pm_user_id: p.pm_user_id || "",
        }}
        initialSyncInfo={p.sync}
        initialClientId={client?.id}
        initialClientName={client?.name}
        onSaved={reloadProjectAndLists}
        onRequestAddContact={(clientId) => setAddContactForClient({ id: clientId, name: clientId === client?.id ? client?.name : undefined })}
      />
      <RecordFormModal
        type="contact"
        open={!!addContactForClient}
        onClose={() => setAddContactForClient(null)}
        defaultClientId={addContactForClient?.id}
        defaultClientName={addContactForClient?.name}
        onCreated={() => {
          setAddContactForClient(null);
          toast.success("Contact added — reopen Edit to pick them as Client Point of Contact");
          reloadProject();
          invalidatePortal.contacts();
        }}
      />
    </div>
  );
}

const Stat = ({ label, value, sub, gated }) => (
  <div className="rounded-md border border-hairline bg-paper p-4" data-testid={gated ? "project-internal-cost-stat" : undefined}>
    <div className="flex items-center justify-between gap-2">
      <div className="text-[10px] font-bold uppercase tracking-wide text-faint">{label}</div>
      {gated && <span className="rounded-full bg-tone-gold-bg px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-tone-gold-text">Internal Only</span>}
    </div>
    <div className="text-xl mt-1 font-mono font-bold text-ink">{value}</div>
    {sub && <div className="text-[11px] font-semibold text-tone-green-text mt-0.5">{sub}</div>}
  </div>
);

// Project Workspace Phase 1 (2026-09), §4 — extracted so the same quote row can render either
// as the prominent "current" item or one of the folded "older" rows in the merged Commercial tab.
const QuoteRow = ({ q, p, client, user, markingWon, markWon, creatingAssignment, createProductionAssignment }) => {
  const isWon = q.outcome === "Won";
  return (
    <div data-testid={`project-quote-row-${q.id}`} className="px-5 py-3.5 text-[13.5px] flex items-center justify-between gap-2">
      <div className="min-w-0">
        <div className="text-ink truncate">{q.name}</div>
        <div className="text-[11px] font-semibold text-faint">{formatMoney(q.quoted_total, q.currency)} · {q.outcome || "Draft"}</div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <Link to={`/commercial-docs?projectId=${p.id}&quoteId=${q.id}&clientId=${client?.id || ""}`} data-testid={`generate-sow-btn-${q.id}`}
          className="inline-flex items-center gap-1 rounded-md border border-brand/50 bg-brand/10 px-2 py-1 text-[11px] font-semibold text-gold-text hover:bg-brand/20">
          <FileText className="h-3 w-3" /> Generate SOW
        </Link>
        {!isWon && (user?.role === "founder" || user?.role === "partner") && (
          <button onClick={() => markWon(q.id)} disabled={markingWon === q.id} data-testid={`mark-quote-won-btn-${q.id}`}
            className="inline-flex items-center gap-1 rounded-md border border-tone-green-text/30 bg-tone-green-bg px-2 py-1 text-[11px] font-semibold text-tone-green-text hover:bg-tone-green-bg/70 disabled:opacity-50">
            {markingWon === q.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />} Mark Won
          </button>
        )}
        {isWon && ["founder", "partner", "pm"].includes(user?.role) && (
          <button onClick={() => createProductionAssignment(q.id)} disabled={creatingAssignment === q.id || p.production_model !== "Production Partner Managed"}
            data-testid={`create-production-assignment-btn-${q.id}`}
            title={p.production_model !== "Production Partner Managed" ? "Set Production Model = Partner Managed to hand off to a production partner" : "Draft a Partner Budget Assignment from this quote's internal production allocation"}
            className="inline-flex items-center gap-1 rounded-md border border-tone-blue-text/30 bg-tone-blue-bg px-2 py-1 text-[11px] font-semibold text-tone-blue-text hover:bg-tone-blue-bg/70 disabled:opacity-40 disabled:cursor-not-allowed">
            {creatingAssignment === q.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <HandCoins className="h-3 w-3" />} Create Production Assignment
          </button>
        )}
      </div>
    </div>
  );
};

const DraftRow = ({ d }) => (
  <div data-testid={`project-draft-row-${d.id}`} className="px-5 py-3.5 text-[13.5px] flex items-center justify-between gap-2">
    <div className="min-w-0">
      <div className="text-ink truncate">{d.quote_name}</div>
      <div className="text-[11px] font-semibold text-faint">{d.grand_total ? formatMoney(d.grand_total, d.currency) : "—"} · updated by {d.updated_by || "—"} · Draft</div>
    </div>
    <Link to={`/commercial?draft_id=${d.id}`} data-testid={`reopen-draft-btn-${d.id}`}
      className="inline-flex items-center gap-1 rounded-md border border-hairline px-2 py-1 text-[11px] font-semibold text-ink-soft hover:border-brand hover:text-gold-text shrink-0">
      <Receipt className="h-3 w-3" /> Reopen in Quote Builder
    </Link>
  </div>
);

// Bug fix (2026-02): a few legacy Notion rows have the literal text "None" typed into a
// rich_text field (a human data-entry habit, not a real value) — treat that the same as blank
// for display purposes everywhere on this page, rather than showing the word "None" verbatim.
const hasContent = (v) => Boolean(v) && String(v).trim().toLowerCase() !== "none";

// Project IA reorganisation, P1 (2026-02): the Brief block (Overview tab) groups Target
// Audience + Creative Concept + Challenges alongside the hand-written Objective/Deliverables
// rows above; the rest of the old "Notes from Notion" fields move to History's "Additional
// Notes" below. `timeline_notes` moved OUT of History and into Workspace's own "Production
// Notes" card (Project Page Redesign, 2026-02) — a closer conceptual fit (shoot-logistics notes
// synced from Notion) than a retrospective note, same underlying field, no data lost.
const BRIEF_NOTES_FIELDS = [
  ["target_audience", "Target Audience"],
  ["creative_concept", "Creative Concept / Tone"],
  ["challenges", "Constraints / Challenges"],
];
const HISTORY_NOTES_FIELDS = [
  ["client_feedback", "Client Feedback"],
  ["partner_agency", "Partner Agency"],
];

// Code-review fix (2026-10, Cursor audit item 8) — every tab key the panels below actually
// render; `activeTab`'s initial state (top of the component) falls back to "overview" for
// anything outside this list.
const VALID_TABS = ["overview", "activity", "workspace", "deliverables", "communication", "commercial", "history"];

// Project Page Redesign (2026-02), Overview tab fix #2: replaces the old empty "Action Centre —
// coming in P2" dashed placeholder box with a small, real, computed card — next phase (from the
// SAME /timeline stages already fetched), crew assignment status, and the latest internal note
// (most recent Internal-visibility comment) — all EXISTING data, no new backend.
// Phase 4 (2026-10, Design System 2.0 visual reskin): the muted "Action Centre arrives in P2"
// caption that used to sit below these rows is removed per the approved spec — the three rows
// themselves (and the data behind them) are unchanged.
const WhatsNextCard = ({ project: p, stages, projectId, crewAssignments }) => {
  const { hasScope } = useAuth();
  // Perf fix (2026-09, NJM OS Phase-0 audit finding): this used to be its OWN
  // `GET /v1/build/assignments?project_id=` fetch — a second round trip for data
  // `GET /v1/projects/{id}` (the page's own main call) already returns as `crew_assignments`.
  const crewCount = hasScope("build.read") ? (crewAssignments || []).length : null;
  const [latestNote, setLatestNote] = useState(null);

  useEffect(() => {
    if (hasScope("project_comments.read")) {
      api.get(`/v1/projects/${projectId}/comments`).then((r) => {
        const internal = (r.data.items || []).filter((c) => c.visibility === "Internal");
        internal.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
        setLatestNote(internal[0] || null);
      }).catch(() => setLatestNote(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const currentIdx = (stages || []).findIndex((s) => s.state === "current");
  const nextStage = currentIdx >= 0 ? stages[currentIdx + 1] : null;

  return (
    <SectionPanel title="What's Next" testId="project-whats-next-section">
      <div className="p-5 space-y-3.5" data-testid="project-whats-next-card">
        {nextStage ? (
          <NextRow tag="PHASE">{p.status || "Current phase"} → move project to <strong>{nextStage.label}</strong>.</NextRow>
        ) : (
          <NextRow tag="PHASE">This project has reached its final phase.</NextRow>
        )}
        <NextRow tag="CREW">{crewCount === null ? "—" : crewCount === 0 ? "No crew assigned yet — assign in Workspace." : `${crewCount} crew assigned — review in Workspace.`}</NextRow>
        <NextRow tag="CLIENT">{latestNote ? `${(latestNote.text || "").slice(0, 100)} (last internal note, ${(latestNote.created_at || "").slice(0, 10)}).` : "No internal notes logged yet — add one in Communication."}</NextRow>
      </div>
    </SectionPanel>
  );
};

const NextRow = ({ tag, children }) => (
  <div className="flex items-start gap-2.5">
    <span className="shrink-0 rounded-full bg-tone-gold-bg px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-tone-gold-text mt-0.5">{tag}</span>
    <span className="text-[13px] text-ink-soft leading-relaxed">{children}</span>
  </div>
);
